<section id="account-overview" class="tw-w-full" data-cy="overview-screen">
  <form [formGroup]="getOverviewForm" class="overview-form">
    <!-- Date From -->
    <mat-form-field appearance="outline" class="tw-w-full">
      <mat-label data-cy="date-from-txt">{{ 'account.statements.datefrom' | translate: 'Date From' }}</mat-label>
      <input
        matInput
        readonly
        [matDatepicker]="fromPicker"
        [max]="maxDate"
        formControlName="dateFrom"
        (click)="fromPicker.open()"
        data-cy="date-from-input"
      >
      <mat-datepicker-toggle matIconSuffix [for]="fromPicker" data-cy="date-from-cal"/>
      <mat-datepicker #fromPicker xPosition="end"/>
    </mat-form-field>

    <!-- Date To -->
    <mat-form-field appearance="outline" class="tw-w-full">
      <mat-label data-cy="date-to-txt">{{ 'account.statements.dateto' | translate: 'Date To' }}</mat-label>
      <input
        matInput
        readonly
        [matDatepicker]="toPicker"
        [max]="maxDate"
        formControlName="dateTo"
        (click)="toPicker.open()"
        data-cy="date-to-input"
      >
      <mat-datepicker-toggle matIconSuffix [for]="toPicker" data-cy="date-to-cal"/>
      <mat-datepicker #toPicker xPosition="end"/>
    </mat-form-field>

    <button
      mat-flat-button
      type="button"
      (click)="searchOverview()"
      [disabled]="getOverviewForm.invalid"
      [ngClass]="{ 'active': getOverviewForm.valid }"
      class="search-overview tw-w-full"
      data-cy="search-btn"
    >
      {{ 'generic.search' | translate: 'Search' }}
    </button>
  </form>

  <!-- Totals -->
  <h4 class="overview-title" data-cy="totals-title">{{ 'generic.totals' | translate: 'Totals' }}</h4>
  <div class="tw-mt-[8px] results">
    <table class="overview-table tw-w-full" data-cy="totals-table">
      <thead>
        <tr>
          <th data-cy="col-type">{{ 'generic.type' | translate: 'Type' }}</th>
          <th data-cy="col-current">{{ 'ui.account.current' | translate: 'Current' }}</th>
        </tr>
      </thead>
      <tbody>
        @if (totals.length) {
          @for (row of totals; track row.label) {
            <tr [attr.data-cy]="'total-' + row.label">
              <td>{{ row.label }}</td>
              <td>${{ row.value | numberWithCommas }}</td>
            </tr>
          }
        } @else {
          <tr><td colspan="2" class="tw-text-center" data-cy="totals-no-data">{{ 'ui.account.no.data.to.display' | translate: 'No Data to display' }}</td></tr>
        }
      </tbody>
    </table>
  </div>

  <!-- Best Bets -->
  <h4 class="overview-title" data-cy="best-bets-title">{{ 'ui.account.best.bets' | translate: 'Best Bets' }}</h4>
  <div class="tw-mt-[8px] results">
    <table class="overview-table tw-w-full" data-cy="best-bets-table">
      <thead>
        <tr>
          <th>{{ 'generic.track' | translate: 'Track' }}</th>
          <th>{{ 'generic.pool' | translate: 'Pool' }}</th>
          <th>{{ 'generic.wagered' | translate: 'Wagered' }}</th>
          <th>{{ 'ui.account.winnings' | translate: 'Winnings' }}</th>
          <th>{{ 'ui.account.win.loss' | translate: 'Win/Loss' }}</th>
          <th>{{ 'ui.account.w.l.percent' | translate: 'W/L Percent' }}</th>
        </tr>
      </thead>
      <tbody>
        @if (bestBets.length) {
          @for (row of bestBets; track trackByBet($index, row)) {
            <tr [attr.data-cy]="'best-bet-' + $index">
              <td>{{ row.eventName | titlecase }}</td>
              <td>{{ row.poolId }}</td>
              <td>${{ row.wagered | numberWithCommas }}</td>
              <td>${{ row.winning | numberWithCommas }}</td>
              <td>${{ row.winLoss | numberWithCommas }}</td>
              <td>{{ row.winLossPercent | number: '1.0-2' }}%</td>
            </tr>
          }
        } @else {
          <tr><td colspan="6" class="tw-text-center" data-cy="best-bets-no-data">{{ 'ui.account.no.data.to.display' | translate: 'No Data to display' }}</td></tr>
        }
      </tbody>
    </table>
  </div>

  <!-- Worst Bets -->
  <h4 class="overview-title" data-cy="worst-bets-title">{{ 'ui.account.worst.bets' | translate: 'Worst Bets' }}</h4>
  <div class="tw-mt-[8px] results">
    <table class="overview-table tw-w-full" data-cy="worst-bets-table">
      <thead>
        <tr>
          <th>{{ 'generic.track' | translate: 'Track' }}</th>
          <th>{{ 'generic.pool' | translate: 'Pool' }}</th>
          <th>{{ 'generic.wagered' | translate: 'Wagered' }}</th>
          <th>{{ 'ui.account.winnings' | translate: 'Winnings' }}</th>
          <th>{{ 'ui.account.win.loss' | translate: 'Win/Loss' }}</th>
          <th>{{ 'ui.account.w.l.percent' | translate: 'W/L Percent' }}</th>
        </tr>
      </thead>
      <tbody>
        @if (worstBets.length) {
          @for (row of worstBets; track trackByBet($index, row)) {
            <tr [attr.data-cy]="'worst-bet-' + $index">
              <td>{{ row.eventName | titlecase }}</td>
              <td>{{ row.poolId }}</td>
              <td>${{ row.wagered | numberWithCommas }}</td>
              <td>${{ row.winning | numberWithCommas }}</td>
              <td>${{ row.winLoss | numberWithCommas }}</td>
              <td>{{ row.winLossPercent | number: '1.0-2' }}%</td>
            </tr>
          }
        } @else {
          <tr><td colspan="6" class="tw-text-center" data-cy="worst-bets-no-data">{{ 'ui.account.no.data.to.display' | translate: 'No Data to display' }}</td></tr>
        }
      </tbody>
    </table>
  </div>

  <!-- Trend charts — placeholder boxes (plotting not yet ported) -->
  <div class="overview-charts">
    @for (title of chartTitles; track title) {
      <div class="chart-box" [attr.data-cy]="'chart-' + title">
        <div class="chart-box__header">{{ title }}</div>
        <div class="chart-box__body">
          <span class="chart-box__empty">{{ 'ui.account.no.data.to.display' | translate: 'No Data to display' }}</span>
        </div>
      </div>
    }
  </div>
</section>
